import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { AuthConfigDto } from '@koverta/shared';
import { LogoMark } from '../components/ui';
import { errorMessage, get, post } from '../lib/api';
import { queryClient } from '../lib/queries';

const ERRORS: Record<string, string> = {
  not_invited:
    "That Google account isn't part of this household. Ask the owner to invite your email in Settings › Household.",
  unverified: 'Your Google email address is not verified.',
  state: 'The sign-in link expired. Please try again.',
  google: 'Google sign-in failed. Please try again.',
};

export function LoginPage() {
  const cfg = useQuery({ queryKey: ['auth-config'], queryFn: () => get<AuthConfigDto>('/api/auth/config') });
  const err = new URLSearchParams(location.search).get('error');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [devErr, setDevErr] = useState<string | null>(null);
  const devLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setDevErr(null);
    try {
      await post('/api/auth/dev-login', { email, name });
      history.replaceState(null, '', '/');
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (x) {
      setDevErr(errorMessage(x));
    }
  };
  return (
    <div className="frame auth">
      <div className="auth-card">
        <span className="logo" style={{ width: 48, height: 48, borderRadius: 14 }}>
          <LogoMark />
        </span>
        <h1>Ovce na broju</h1>
        <p>Envelope budgeting for two. Every dinar and every euro gets a job.</p>
        {err ? (
          <div className="banner warn" style={{ margin: '0 0 16px' }} role="alert">
            {ERRORS[err] ?? 'Sign-in failed.'}
          </div>
        ) : null}
        {cfg.data?.google ? (
          <a className="btn primary big gbtn" href="/api/auth/google">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path
                fill="#FFC107"
                d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
              />
              <path
                fill="#FF3D00"
                d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
              />
              <path
                fill="#4CAF50"
                d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"
              />
              <path
                fill="#1976D2"
                d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
              />
            </svg>
            Continue with Google
          </a>
        ) : null}
        {cfg.data?.devLogin ? (
          <>
            {cfg.data.google ? <div className="auth-sep">development sign-in</div> : null}
            <form className="wizard" onSubmit={devLogin}>
              <label className="field">
                Email
                <input
                  className="input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </label>
              <label className="field">
                Name
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="given-name"
                />
              </label>
              {devErr ? <div className="err-t">{devErr}</div> : null}
              <button className="btn primary big" type="submit">
                Sign in (dev)
              </button>
            </form>
          </>
        ) : null}
        {cfg.data && !cfg.data.google && !cfg.data.devLogin ? (
          <p>Sign-in is not configured. Set GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET.</p>
        ) : null}
      </div>
    </div>
  );
}
